import { graphql } from "gatsby";
import {
    ContentfulRichTextGatsbyReference,
    RenderRichTextData,
    renderRichText
} from "gatsby-source-contentful/rich-text";
import * as React from "react";
import { BlogLinks } from "../components/blog/links";
import { CityLinks } from "../components/destination/city-links";
import { Faq } from "../components/destination/faq";
import { DestinationPageHero } from "../components/destination/hero";
import { LinkType, Links } from "../components/destination/links";
import { DestinationOverviewProps } from "../components/destination/overview";
import { RegionResortCore } from "../components/destination/region-resort-core";
import { Layout } from "../components/layout/layout";
import { ShareContent } from "../components/layout/share";
import { DestinationCards } from "../components/shared/cards";
import { TemperatureUnitContext } from "../context/app-context";
import { useContentful } from "../hooks/useContentful";
import { useLoc } from "../hooks/useLoc";
import { LocationType } from "../interfaces";
import firstCapital from "../services/firstCapitalCase";
import textParser from "../services/textParser";
import { Region, RegionMonth } from "./fragments/region";

type RegionType = {
    data: Query;
    location: LocationType;
    pageContext: {
        month: string;
        slug: string;
        lang?: string;
        fullSlug: string;
        additionalText?: { raw: string };
        links?: LinkType[];
        year: number;
    };
};

const RegionPage = ({ data, location, pageContext }: RegionType) => {
    //console.log(pageContext, data.regionsJson.writeYearIntoTitle);
    const loc = useLoc(pageContext.lang || "en");
    const [units] = React.useContext(TemperatureUnitContext);
    const { getOptions } = useContentful();
    let weatherOrClimate =
        pageContext.month == "climate"
            ? loc("common.climate")
            : loc("common.weather");

    const faqs = data.regionsMonthsJson.questions.map(
        (question: string, idx: number) => {
            return {
                question: question,
                answer: data.regionsMonthsJson.answers[idx]
            };
        }
    );
    faqs.splice(0, 0, {
        question: firstCapital(weatherOrClimate) + " " + loc("common.overview"),
        answer: data.regionsMonthsJson.header
    });
    let header = `${data.regionsJson.name} ${weatherOrClimate}`;
    header +=
        pageContext.month != "climate"
            ? ` ${loc("common.in")} ${loc(
                  "common.months." + pageContext.month
              )}`
            : "";
    if (data.regionsJson.writeYearIntoTitle) {
        header += " " + pageContext.year;
    }
    const destinationInfoHeader =
        firstCapital(weatherOrClimate) + " " + loc("common.overviewAndFaqs");
    const og = `og/reg-${pageContext.slug}-weather.png`;
    const destinationsInText = `${loc("common.destinationsIn")} ${
        data.regionsJson.name
    }`;
    const destinationsSimilar = `${loc(
        "common.otherDestinationsWithSimilar"
    )} ${data.regionsJson.name}`;
    const overviewProperties: DestinationOverviewProps = {
        destinationsInText,
        similarDestinationsText: destinationsSimilar,
        weatherOverviewText: destinationInfoHeader
    };
    const description = textParser({
        text: data.regionsMonthsJson.header,
        units: units,
        noNbsp: true
    });
    const citiesInText = `${loc("common.citiesIn")} ${data.regionsJson.name}`;
    const introContent =
        data.contentfulRegionSummary?.summary &&
        renderRichText(data.contentfulRegionSummary.summary, getOptions());
    return (
        <Layout
            path={"/" + pageContext.fullSlug}
            title={header}
            faqs={faqs}
            og={og}
            description={description}
        >
            <DestinationPageHero
                className="lg:hidden"
                header={header}
                bgImage={
                    data.regionsJson.heroImgFull.childImageSharp.gatsbyImageData
                }
                region={data.regionsJson}
            />
            <DestinationPageHero
                className="lgmax:hidden"
                header={header}
                bgImage={
                    data.regionsJson.heroImg.childImageSharp.gatsbyImageData
                }
                region={data.regionsJson}
            />

            <RegionResortCore
                {...{ pageContext }}
                month={pageContext.month}
                locationJson={data.regionsJson}
                overviewProperties={overviewProperties}
                introContent={introContent || description}
                introTitle={data.regionsJson.name}
            />

            <div className="h-16"></div>

            <h2 className="w-destination-header font-destination-header m-auto mb-12">
                <a
                    id="destinations-in"
                    className="relative top-[-100px] md:top-[-120px]"
                />
                {destinationsInText}
            </h2>
            <DestinationCards
                regionsOrResorts={data.regionsJson.resorts}
                isResorts={true}
                className=""
            />
            <div className="h-16"></div>

            <h2 className="w-destination-header font-destination-header m-auto mb-12">
                <a
                    id="destinations-similar"
                    className="relative top-[-100px] md:top-[-120px]"
                />
                {destinationsSimilar}
            </h2>
            <DestinationCards
                regionsOrResorts={data.regionsMonthsJson.similar}
                className=""
            />
            {data.regionsJson.cities?.length && (
                <CityLinks
                    headerText={citiesInText}
                    cities={data.regionsJson.cities}
                />
            )}

            <hr className="m-auto mt-16 mb-12 w-full xm:w-3/4" />

            <h2 className="w-destination-header font-destination-header m-auto mt-16">
                <a
                    id="weather-overview"
                    className="relative top-[-100px] md:top-[-120px]"
                />
                {destinationInfoHeader}
            </h2>
            <Faq faqs={faqs} />
            <Links links={pageContext.links}></Links>
            <BlogLinks continent={data.regionsJson.country.continent} />
            <div className="h-24"></div>
            <ShareContent active={true} partOfPage={true} />
        </Layout>
    );
};

interface Query {
    regionsJson: Region;
    regionsMonthsJson: RegionMonth;
    contentfulRegionSummary?: {
        summary?: RenderRichTextData<ContentfulRichTextGatsbyReference>;
    };
}

export const query = graphql`
    query QueryRegion($monthShort: String, $slug: String) {
        regionsJson(slug: { eq: $slug }) {
            ...RegionFull
        }
        regionsMonthsJson(month: { eq: $monthShort }, slug: { eq: $slug }) {
            ...RegionMonth
        }
        contentfulRegionSummary(slug: { eq: $slug }) {
            summary {
                raw
            }
        }
    }
`;

export default RegionPage;
